import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, FileText } from 'lucide-react';
import { normNombre, buscarActaDeAsignacion } from '../utils/actasMatch';

const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

/** Primer paso para generar un acta: elegir a qué asignación (equipo + persona) corresponde. */
export default function SeleccionarAsignacionActaModal({ isOpen, onClose, asignaciones, actasPorAsignacion, onSelect }) {
    const [q, setQ] = useState('');
    const [soloSinActa, setSoloSinActa] = useState(true);

    useEffect(() => { if (isOpen) { setQ(''); setSoloSinActa(true); } }, [isOpen]);

    const filas = useMemo(() => {
        const t = normNombre(q);
        return (asignaciones || [])
            .map((a) => ({ a, tieneActa: !!buscarActaDeAsignacion(a, actasPorAsignacion || {}) }))
            .filter(({ a, tieneActa }) => {
                if (soloSinActa && tieneActa) return false;
                if (!t) return true;
                const txt = normNombre(`${a.activo?.codigo || ''} ${a.activo?.categoria || ''} ${a.activo?.nombre || ''} ${a.usuario?.nombre || ''} ${a.usuario?.apellido || ''} ${a.usuario?.email || ''}`);
                return txt.includes(t);
            })
            .sort((x, y) => Number(x.tieneActa) - Number(y.tieneActa))
            .slice(0, 60);
    }, [asignaciones, actasPorAsignacion, q, soloSinActa]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
            <div role="dialog" aria-label="Generar nueva acta" className="bg-white dark:bg-gray-800 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Generar nueva acta</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Elige la asignación (equipo y persona) para la que quieres generar el acta.</p>
                    </div>
                    <button onClick={onClose} aria-label="Cerrar" className="p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>
                <div className="p-5 pb-3 space-y-3">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, persona, correo o categoría…"
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" checked={soloSinActa} onChange={(e) => setSoloSinActa(e.target.checked)} className="h-4 w-4" />
                        Mostrar solo las que no tienen acta
                    </label>
                </div>
                <div className="px-5 pb-5 overflow-y-auto space-y-2">
                    {filas.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No hay asignaciones con ese criterio.</p>}
                    {filas.map(({ a, tieneActa }) => (
                        <button key={a.id} type="button" onClick={() => onSelect(a)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50/40 dark:hover:bg-gray-700/40 text-left transition-colors">
                            <span className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600"><FileText size={18} /></span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-gray-900 dark:text-white truncate">{a.activo?.codigo || a.activo?.nombre || 'Insumo'} <span className="font-normal text-gray-500">· {a.activo?.categoria}</span></span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{[a.usuario?.nombre, a.usuario?.apellido].filter(Boolean).join(' ')} {a.usuario?.email ? `· ${a.usuario.email}` : ''}</span>
                            </span>
                            <span className="text-right shrink-0">
                                <span className={`block text-[11px] font-semibold ${tieneActa ? 'text-emerald-600' : 'text-orange-600'}`}>{tieneActa ? 'Ya tiene acta' : 'Sin acta'}</span>
                                <span className="block text-[11px] text-gray-400">{fecha(a.fechaAsignacion)}</span>
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body,
    );
}
